import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Star,
  StarOff,
  Settings,
  Package,
  Truck,
  Calculator,
  BookOpen,
  TrendingUp,
  Scale,
  DollarSign,
  BarChart3,
  AlertTriangle,
  Upload,
  Receipt,
  StickyNote,
  Database,
  HeartPulse,
  LayoutDashboard,
  Users,
  ArrowRight,
  X,
  Plus,
  ChevronUp,
  ChevronDown,
  Layers,
  FileText,
  Zap,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  LayoutDashboard, Users, Package, Truck, Calculator, BookOpen,
  TrendingUp, Scale, DollarSign, BarChart3, AlertTriangle, Upload,
  Receipt, StickyNote, Database, HeartPulse,
};

// Color palette per category
const PANEL_COLORS = [
  { bg: "from-blue-500/10 to-cyan-500/5", border: "border-blue-500/20", icon: "text-blue-500" },
  { bg: "from-emerald-500/10 to-green-500/5", border: "border-emerald-500/20", icon: "text-emerald-500" },
  { bg: "from-violet-500/10 to-purple-500/5", border: "border-violet-500/20", icon: "text-violet-500" },
  { bg: "from-rose-500/10 to-pink-500/5", border: "border-rose-500/20", icon: "text-rose-500" },
  { bg: "from-orange-500/10 to-amber-500/5", border: "border-orange-500/20", icon: "text-orange-500" },
  { bg: "from-teal-500/10 to-cyan-500/5", border: "border-teal-500/20", icon: "text-teal-500" },
  { bg: "from-indigo-500/10 to-blue-500/5", border: "border-indigo-500/20", icon: "text-indigo-500" },
  { bg: "from-amber-500/10 to-yellow-500/5", border: "border-amber-500/20", icon: "text-amber-600" },
];
const QV_COLORS = [
  { bg: "from-cyan-500/10 to-teal-500/5", border: "border-cyan-500/20", icon: "text-cyan-600" },
  { bg: "from-sky-500/10 to-blue-500/5", border: "border-sky-500/20", icon: "text-sky-500" },
  { bg: "from-purple-500/10 to-fuchsia-500/5", border: "border-purple-500/20", icon: "text-purple-500" },
  { bg: "from-lime-500/10 to-green-500/5", border: "border-lime-500/20", icon: "text-lime-600" },
];
const REPORT_COLORS = [
  { bg: "from-amber-500/10 to-orange-500/5", border: "border-amber-500/20", icon: "text-amber-600" },
  { bg: "from-red-500/10 to-rose-500/5", border: "border-red-500/20", icon: "text-red-500" },
  { bg: "from-fuchsia-500/10 to-pink-500/5", border: "border-fuchsia-500/20", icon: "text-fuchsia-500" },
  { bg: "from-yellow-500/10 to-amber-500/5", border: "border-yellow-500/20", icon: "text-yellow-600" },
];

const STAT_MAP = {
  dashboard: { statKey: "total_orders", label: "Parcels" },
  products: { statKey: "total_products", label: "Products" },
  sales: { statKey: "total_sales", label: "Records" },
  error_resolution: { statKey: "error_count", label: "Errors" },
  errors_sales: { statKey: "negative_profit", label: "Neg. Profit" },
  ledger: { statKey: "total_clients", label: "Clients" },
};

const CATEGORY_TABS = [
  { key: "all", label: "All", icon: Star },
  { key: "panel", label: "Panels", icon: Layers },
  { key: "quick_view", label: "Quick Views", icon: Zap },
  { key: "report", label: "Reports", icon: FileText },
];

function getColorForItem(item, idx) {
  if (item.category === "panel") return PANEL_COLORS[idx % PANEL_COLORS.length];
  if (item.category === "quick_view") return QV_COLORS[idx % QV_COLORS.length];
  return REPORT_COLORS[idx % REPORT_COLORS.length];
}

function getCategoryBadge(category) {
  if (category === "panel") return { label: "Panel", className: "bg-blue-500/10 text-blue-600 border-blue-500/20" };
  if (category === "quick_view") return { label: "Quick View", className: "bg-cyan-500/10 text-cyan-600 border-cyan-500/20" };
  return { label: "Report", className: "bg-amber-500/10 text-amber-600 border-amber-500/20" };
}

export default function FavoritesPage() {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [availablePanels, setAvailablePanels] = useState([]);
  const [availableQuickViews, setAvailableQuickViews] = useState([]);
  const [availableReports, setAvailableReports] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [settingsTab, setSettingsTab] = useState("panel");
  const token = localStorage.getItem("token");

  const fetchFavorites = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/favorites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFavorites(data.favorites || []);
        setAvailablePanels(data.available_panels || []);
        setAvailableQuickViews(data.available_quick_views || []);
        setAvailableReports(data.available_reports || []);
      }
    } catch (err) {
      console.error("Failed to fetch favorites:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/favorites/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStats(await res.json());
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  }, [token]);

  useEffect(() => {
    fetchFavorites();
    fetchStats();
  }, [fetchFavorites, fetchStats]);

  const toggleFavorite = async (key, isCurrentlyFav) => {
    try {
      if (isCurrentlyFav) {
        await fetch(`${BACKEND_URL}/api/favorites/${key}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Removed from favorites");
      } else {
        await fetch(`${BACKEND_URL}/api/favorites`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ key }),
        });
        toast.success("Added to favorites");
      }
      fetchFavorites();
    } catch (err) {
      toast.error("Failed to update favorites");
    }
  };

  const moveFavorite = async (index, direction) => {
    const filtered = getFilteredFavorites();
    const targetIdx = direction === "up" ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= filtered.length) return;
    // Swap in the full favorites array
    const fullIdxA = favorites.findIndex(f => f.key === filtered[index].key);
    const fullIdxB = favorites.findIndex(f => f.key === filtered[targetIdx].key);
    const newFavs = [...favorites];
    [newFavs[fullIdxA], newFavs[fullIdxB]] = [newFavs[fullIdxB], newFavs[fullIdxA]];
    setFavorites(newFavs);
    try {
      await fetch(`${BACKEND_URL}/api/favorites/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ keys: newFavs.map(f => f.key) }),
      });
    } catch (err) { console.error("Reorder failed:", err); }
  };

  const isFavorited = (key) => favorites.some(f => f.key === key);

  const getFilteredFavorites = () => {
    if (activeTab === "all") return favorites;
    return favorites.filter(f => f.category === activeTab);
  };

  const getSettingsItems = () => {
    if (settingsTab === "panel") return availablePanels;
    if (settingsTab === "quick_view") return availableQuickViews;
    return availableReports;
  };

  const handleNavigate = (fav) => {
    const path = fav.path;
    if (path.includes("?")) {
      const [basePath, queryStr] = path.split("?");
      navigate(`${basePath}?${queryStr}`);
    } else {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="favorites-loading">
        <div className="animate-pulse text-muted-foreground">Loading favorites...</div>
      </div>
    );
  }

  const filteredFavorites = getFilteredFavorites();
  const settingsItems = getSettingsItems();
  const categoryCount = (cat) => favorites.filter(f => f.category === cat).length;

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-5" data-testid="favorites-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-foreground">Favorites</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {favorites.length} item{favorites.length !== 1 ? "s" : ""} saved
          </p>
        </div>
        <Button
          variant={settingsOpen ? "default" : "outline"}
          size="sm"
          onClick={() => setSettingsOpen(!settingsOpen)}
          className="gap-2"
          data-testid="favorites-settings-btn"
        >
          {settingsOpen ? <X className="w-4 h-4" /> : <Settings className="w-4 h-4" />}
          {settingsOpen ? "Done" : "Configure"}
        </Button>
      </div>

      {/* Category Tabs */}
      <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1 w-fit" data-testid="favorites-category-tabs">
        {CATEGORY_TABS.map(tab => {
          const count = tab.key === "all" ? favorites.length : categoryCount(tab.key);
          const isActive = activeTab === tab.key;
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all
                ${isActive
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
                }
              `}
              data-testid={`tab-${tab.key}`}
            >
              <TabIcon className="w-3.5 h-3.5" />
              {tab.label}
              {count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ml-0.5 ${isActive ? "bg-foreground/10" : "bg-muted"}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Settings Panel */}
      {settingsOpen && (
        <Card className="p-4 border-dashed border-2 border-muted-foreground/20 bg-muted/20" data-testid="favorites-settings-panel">
          {/* Settings sub-tabs */}
          <div className="flex items-center gap-1 mb-4">
            {[
              { key: "panel", label: "Panels", icon: Layers },
              { key: "quick_view", label: "Quick Views", icon: Zap },
              { key: "report", label: "Reports", icon: FileText },
            ].map(st => (
              <button
                key={st.key}
                onClick={() => setSettingsTab(st.key)}
                className={`
                  flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider transition-all
                  ${settingsTab === st.key
                    ? "bg-foreground/10 text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                  }
                `}
                data-testid={`settings-tab-${st.key}`}
              >
                <st.icon className="w-3 h-3" />
                {st.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {settingsItems.map(item => {
              const isFav = isFavorited(item.key);
              const IconComp = ICON_MAP[item.icon] || LayoutDashboard;
              return (
                <button
                  key={item.key}
                  onClick={() => toggleFavorite(item.key, isFav)}
                  className={`
                    flex items-center gap-2 px-3 py-2.5 rounded-lg text-left text-sm transition-all
                    ${isFav
                      ? "bg-orange-500/10 border border-orange-500/30 text-orange-700 dark:text-orange-400"
                      : "bg-background border border-border text-muted-foreground hover:border-foreground/20 hover:text-foreground"
                    }
                  `}
                  data-testid={`toggle-favorite-${item.key}`}
                >
                  {isFav ? (
                    <Star className="w-3.5 h-3.5 fill-orange-500 text-orange-500 flex-shrink-0" />
                  ) : (
                    <StarOff className="w-3.5 h-3.5 flex-shrink-0" />
                  )}
                  <IconComp className="w-3.5 h-3.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="truncate font-medium block">{item.label}</span>
                    {item.parent && (
                      <span className="text-[10px] text-muted-foreground/70 truncate block">{item.parent}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Favorites Grid */}
      {filteredFavorites.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-2" data-testid="favorites-empty">
          <Star className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
          <h3 className="text-lg font-semibold text-muted-foreground mb-2">
            {activeTab === "all" ? "No favorites yet" : `No ${activeTab.replace("_", " ")} favorites`}
          </h3>
          <p className="text-sm text-muted-foreground/70 mb-4">
            Click "Configure" to add items for quick access
          </p>
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} className="gap-2">
            <Settings className="w-4 h-4" /> Configure
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="favorites-grid">
          {filteredFavorites.map((fav, idx) => {
            const colorScheme = getColorForItem(fav, idx);
            const IconComp = ICON_MAP[fav.icon] || LayoutDashboard;
            const statInfo = STAT_MAP[fav.key];
            const statValue = statInfo ? stats[statInfo.statKey] : null;
            const badge = getCategoryBadge(fav.category);

            return (
              <Card
                key={fav.key}
                className={`
                  group relative overflow-hidden cursor-pointer
                  bg-gradient-to-br ${colorScheme.bg} ${colorScheme.border}
                  hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5
                `}
                onClick={() => handleNavigate(fav)}
                data-testid={`favorite-card-${fav.key}`}
              >
                <div className="p-5">
                  {/* Reorder controls */}
                  {settingsOpen && (
                    <div className="absolute top-2 right-2 flex flex-col gap-0.5 z-10" onClick={e => e.stopPropagation()}>
                      <button onClick={() => moveFavorite(idx, "up")} disabled={idx === 0}
                        className="p-1 rounded hover:bg-black/10 disabled:opacity-20 transition-colors"
                        data-testid={`move-up-${fav.key}`}>
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => moveFavorite(idx, "down")} disabled={idx === filteredFavorites.length - 1}
                        className="p-1 rounded hover:bg-black/10 disabled:opacity-20 transition-colors"
                        data-testid={`move-down-${fav.key}`}>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  {/* Top row: icon + category badge */}
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-background/60 backdrop-blur-sm ${colorScheme.border}`}>
                      <IconComp className={`w-5 h-5 ${colorScheme.icon}`} />
                    </div>
                    {activeTab === "all" && (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.className}`}>
                        {badge.label}
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="font-semibold text-foreground text-sm mb-0.5">
                    {fav.label}
                  </h3>
                  {fav.parent && (
                    <p className="text-[11px] text-muted-foreground/60 mb-1">{fav.parent}</p>
                  )}
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                    {fav.description}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between">
                    {statValue !== null && statValue !== undefined ? (
                      <span className="text-xs font-medium text-muted-foreground bg-background/50 px-2 py-1 rounded-md">
                        {statInfo.label}: {typeof statValue === "number" ? statValue.toLocaleString() : statValue}
                      </span>
                    ) : <span />}
                    <ArrowRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-foreground/60 group-hover:translate-x-0.5 transition-all" />
                  </div>
                </div>
              </Card>
            );
          })}

          {/* Add More card */}
          {!settingsOpen && (
            <Card
              className="border-dashed border-2 border-muted-foreground/15 hover:border-muted-foreground/30 cursor-pointer transition-all group"
              onClick={() => setSettingsOpen(true)}
              data-testid="add-more-favorites"
            >
              <div className="p-5 flex flex-col items-center justify-center h-full min-h-[160px]">
                <Plus className="w-8 h-8 text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors mb-2" />
                <span className="text-xs text-muted-foreground/50 group-hover:text-muted-foreground/70 font-medium transition-colors">
                  Add More
                </span>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
